import type React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { formatAdminNumber } from '../../../../utils/numberFormat';
import type { VideoServiceHealth } from '../types';

/** Models per operation and mode with their token price, as users will see them. */
export const VideoServiceOverview: React.FC<{ health: VideoServiceHealth | undefined }> = ({
  health,
}) => (
  <Box className="grid grid-cols-1 gap-4 lg:grid-cols-2">
    {(health?.operations ?? []).map((operation) => (
      <Box
        key={operation.capabilityCode}
        className="rounded-xl bg-white p-4 shadow-sm dark:bg-slate-800"
      >
        <Box className="mb-2 flex items-center justify-between gap-2">
          <Typography className="font-semibold text-gray-900 dark:text-slate-100">
            {operation.name}
          </Typography>
          <Chip
            size="small"
            label={operation.isActive ? 'Active' : 'Inactive'}
            color={operation.isActive ? 'success' : 'default'}
            variant="outlined"
          />
        </Box>
        {operation.models.length === 0 ? (
          <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
            No active model mapped.
          </Typography>
        ) : (
          <Box component="ul" className="m-0 space-y-1 p-0">
            {operation.models.map((model) => (
              <li
                key={`${model.providerCode}:${model.modelCode}:${model.costTier}`}
                className="flex flex-wrap items-center gap-2 text-sm"
              >
                <Chip size="small" label={model.costTier} />
                <span className="font-mono text-gray-800 dark:text-slate-200">
                  {model.modelCode}
                </span>
                {model.resolutionLabel && (
                  <Chip size="small" variant="outlined" label={model.resolutionLabel} />
                )}
                <span
                  className={`ml-auto ${model.pricing ? 'text-gray-500 dark:text-slate-400' : 'text-amber-600'}`}
                >
                  {model.pricing
                    ? `${formatAdminNumber(model.pricing.tokensPerSecond)} credits/s` +
                      (model.pricing.tokensPerVideo
                        ? ` + ${formatAdminNumber(model.pricing.tokensPerVideo)}/video`
                        : '')
                    : 'no video price'}
                </span>
              </li>
            ))}
          </Box>
        )}
      </Box>
    ))}
  </Box>
);
